import { HttpClient } from '@angular/common/http';
import { inject, signal, Service } from '@angular/core';
import { createHttpContext } from '@app/core/http/http-context';
import { environment } from '@env/environment';
import { firstValueFrom, Observable, of, timer } from 'rxjs';
import { map, retry, tap } from 'rxjs/operators';

import { __ } from '../functions/object.functions';
import { tenantNameFromHostname } from '../functions/tenant-hostname.function';
import { Tenant } from '../models/classes/Tenant';
import { normalizeLifecycleState } from '../tenant-lifecycle/tenant-lifecycle';
import { ITenantService } from './itenant.service';
import { Response } from './local/Response';

const tenant_key = 'hypecast-tenant-ag8asigy47e3zlmz5zu';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

type TenantCache = {
  tenant: Tenant;
  subdomain: string;
  expiresAt?: string;
};

@Service({ autoProvided: false })
export class TenantService implements ITenantService {
  protected readonly httpClient = inject(HttpClient);
  private readonly tenantState = signal<Tenant | null>(null);
  private tenantLoad: Promise<Tenant> | null = null;

  readonly tenantSignal = this.tenantState.asReadonly();

  loadTenant(): Promise<Tenant> {
    const currentTenant = this.tenantSignal();
    if (currentTenant !== null) {
      return Promise.resolve(currentTenant);
    }

    this.tenantLoad ??= firstValueFrom(this.resolveTenant()).catch((error: unknown) => {
      this.tenantLoad = null;
      throw error;
    });
    return this.tenantLoad;
  }

  async refreshTenant(): Promise<Tenant | null> {
    if (environment.environmentName === 'DEV') {
      return this.tenantSignal();
    }
    try {
      return await firstValueFrom(this.fetchTenant(this.getTenantName()));
    } catch {
      return this.tenantSignal();
    }
  }

  getTenant(): Tenant | null {
    return this.tenantSignal();
  }

  getTenantName(): string {
    if (environment.environmentName === 'DEV' && !__.IsNullOrUndefined(environment.subdomain)) {
      return environment.subdomain!;
    }

    return tenantNameFromHostname(window.location.hostname);
  }

  setLogo(logo: string): void {
    const currentTenant = this.tenantSignal();
    if (currentTenant === null) {
      return;
    }

    const updatedTenant = Object.assign(new Tenant(), currentTenant, { logoUrl: logo });
    this.setTenantState(updatedTenant);
    this.writeCache(updatedTenant, this.getTenantName());
  }

  uploadTenantLogo(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);

    const subdomain = this.getTenantName();
    return this.httpClient
      .post<Response<string>>(`${environment.authorizationServerUrl}api/v1/tenants/${subdomain}/logo`, formData, {
        context: createHttpContext({
          skipApiPrefix: true,
          skipErrorHandler: true,
          skipAuthorization: true,
        }),
      })
      .pipe(map((response: Response<string>) => response.data));
  }

  resetTenant(): void {
    sessionStorage.removeItem(tenant_key);
    localStorage.removeItem(tenant_key);
    this.tenantState.set(null);
    this.tenantLoad = null;
  }

  private resolveTenant(): Observable<Tenant> {
    const currentTenant = this.tenantSignal();
    if (currentTenant !== null) {
      return of(currentTenant);
    }

    const subdomain = this.getTenantName();
    const cached = this.readCache();

    if (environment.environmentName === 'DEV') {
      const localTenant = Object.assign(new Tenant(), {
        tenantName: environment.subdomain,
        apiUrl: environment.serverUrl,
        cdnUrl: environment.serverUrlCDN,
        clientId: environment.clientId,
        companyName: environment.subdomain,
        logoUrl: '',
      } as Tenant);

      this.setTenantState(localTenant);
      return of(localTenant);
    }

    const isCacheValid =
      cached !== null &&
      cached.subdomain === subdomain &&
      !__.IsNullOrUndefined(cached.expiresAt) &&
      new Date(cached.expiresAt).getTime() > Date.now();
    const hasStaleCacheForSubdomain = cached !== null && cached.subdomain === subdomain && !isCacheValid;

    if (hasStaleCacheForSubdomain) {
      this.setTenantState(cached.tenant);
      this.refreshTenantInBackground(subdomain);
      return of(cached.tenant);
    }

    if (isCacheValid) {
      this.setTenantState(cached.tenant);
      // A trial or winding-down tenant changes state on its own schedule, so a
      // cached lifecycle must not stay authoritative for the whole cache TTL.
      if (normalizeLifecycleState(cached.tenant.lifecycleState) !== 'Active') {
        this.refreshTenantInBackground(subdomain);
      }
      return of(cached.tenant);
    }

    return this.fetchTenant(subdomain);
  }

  private fetchTenant(subdomain: string): Observable<Tenant> {
    return this.httpClient
      .get<Response<Tenant>>(`${environment.masterServerUrl}tenants/${subdomain}`, {
        context: createHttpContext({
          skipApiPrefix: true,
          skipAccessToken: true,
          skipAuthorization: true,
        }),
      })
      .pipe(
        retry({ count: 3, delay: (_error, retryCount) => timer(retryCount * 2000) }),
        map((response: Response<Tenant>) => response.data),
        tap((tenant) => {
          this.setTenantState(tenant);
          this.writeCache(tenant, subdomain);
        }),
      );
  }

  private refreshTenantInBackground(subdomain: string): void {
    void firstValueFrom(this.fetchTenant(subdomain)).catch(() => {
      // Stale cache remains valid for this session when the background refresh fails.
    });
  }

  private setTenantState(tenant: Tenant): void {
    this.tenantState.set(tenant);
  }

  private readCache(): TenantCache | null {
    try {
      return JSON.parse(localStorage.getItem(tenant_key)!) as TenantCache | null;
    } catch {
      localStorage.removeItem(tenant_key);
      return null;
    }
  }

  private writeCache(tenant: Tenant, subdomain: string): void {
    localStorage.setItem(
      tenant_key,
      JSON.stringify({
        tenant,
        subdomain,
        expiresAt: new Date(Date.now() + CACHE_TTL_MS).toISOString(),
      } satisfies TenantCache),
    );
  }
}
